import type { ComponentChildren, JSX } from 'preact';
import type { Paisa } from '@khata/core';
import { mLong, num, taka } from '../lib/format.js';
import { listPeriod, type ListPeriod } from '../lib/ui.js';
import { t } from '../lib/i18n.js';
import { setShowVoided, showVoided } from '../lib/ui.js';

export function Kpi(props: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'good' | 'bad' | 'ink';
}) {
  return (
    <div>
      <div class="kpi-label">{props.label}</div>
      <div class={'kpi-value ' + (props.tone ?? '')}>{props.value}</div>
      {props.sub !== undefined && <div class="kpi-sub">{props.sub}</div>}
    </div>
  );
}

export function PageHead(props: {
  title: string;
  kicker?: string;
  sub?: string;
  children?: ComponentChildren;
}) {
  return (
    <header class="page-head">
      <div>
        {props.kicker && <div class="page-kicker">{props.kicker}</div>}
        <h1 class="page-title">{props.title}</h1>
        {props.sub && <div class="page-sub">{props.sub}</div>}
      </div>
      {props.children && <div class="head-actions">{props.children}</div>}
    </header>
  );
}

export interface RowProps {
  title: ComponentChildren;
  sub?: ComponentChildren;
  amount?: string;
  amountTone?: 'good' | 'bad' | 'ink' | 'muted';
  amountSub?: ComponentChildren;
  amountSubTone?: 'good' | 'bad' | 'ink' | 'muted';
  onClick?: () => void;
  strike?: boolean;
}

export function Row(p: RowProps) {
  const inner = (
    <>
      <div class={'row-main' + (p.strike ? ' strike' : '')}>
        <div class="row-title">{p.title}</div>
        {p.sub !== undefined && <div class="row-sub">{p.sub}</div>}
      </div>
      {(p.amount !== undefined || p.amountSub !== undefined) && (
        <div class={'row-side' + (p.strike ? ' strike' : '')}>
          {p.amount !== undefined && (
            <div class={'row-amt ' + (p.amountTone ?? 'ink')}>{p.amount}</div>
          )}
          {p.amountSub !== undefined && (
            <div class={'row-amt-sub ' + (p.amountSubTone ?? 'muted')}>{p.amountSub}</div>
          )}
        </div>
      )}
    </>
  );
  return p.onClick ? (
    <button type="button" class="row" onClick={p.onClick}>
      {inner}
    </button>
  ) : (
    <div class="row">{inner}</div>
  );
}

export function Empty(props: { children: ComponentChildren }) {
  return <div class="empty">{props.children}</div>;
}

export function Chips<T extends string>(props: {
  options: ReadonlyArray<{ key: T; label: string }>;
  value: T;
  onChange: (k: T) => void;
}) {
  return (
    <div class="chips" role="group">
      {props.options.map((o) => (
        <button
          key={o.key}
          type="button"
          class="chip"
          aria-pressed={o.key === props.value}
          onClick={() => props.onChange(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Field(props: {
  label: string;
  children: ComponentChildren;
  error?: boolean;
  help?: string;
}) {
  return (
    <div class="field">
      <label>{props.label}</label>
      {props.children}
      {props.help && <div class="help">{props.help}</div>}
    </div>
  );
}

type InputProps = Omit<JSX.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onInput'> & {
  value: string;
  onInput: (v: string) => void;
  invalid?: boolean;
};

export function TextInput({ value, onInput, invalid, ...rest }: InputProps) {
  return (
    <input
      class="input"
      value={value}
      aria-invalid={invalid ? 'true' : undefined}
      onInput={(e) => onInput((e.target as HTMLInputElement).value)}
      {...rest}
    />
  );
}

/** Taka amount typed as text ("1,234.50"); parsed on save. */
export function MoneyInput(props: InputProps) {
  return <TextInput inputMode="decimal" placeholder="0" {...props} />;
}

/** Quantity typed as text ("1.5"); parsed on save. */
export function QtyInput(props: InputProps) {
  return <TextInput inputMode="decimal" placeholder="0" {...props} />;
}

export function DateInput(props: InputProps) {
  return <TextInput type="date" {...props} />;
}

type SelectProps = Omit<JSX.SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'> & {
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  children: ComponentChildren;
};

export function Select({ value, onChange, invalid, children, ...rest }: SelectProps) {
  return (
    <select
      class="input"
      value={value}
      aria-invalid={invalid ? 'true' : undefined}
      onChange={(e) => onChange((e.target as HTMLSelectElement).value)}
      {...rest}
    >
      {children}
    </select>
  );
}

export function Money(props: { value: Paisa; tone?: 'good' | 'bad' | 'ink' | 'muted' }) {
  return <span class={'mono ' + (props.tone ?? '')}>{taka(props.value)}</span>;
}

/** "Show voided" chip for entry lists. */
export function VoidedToggle() {
  const on = showVoided.value;
  return (
    <div class="list-tools">
      <button
        type="button"
        class="chip"
        aria-pressed={on}
        data-testid="show-voided"
        onClick={() => setShowVoided(!on)}
      >
        {t('list_showVoided')}
      </button>
    </div>
  );
}

/** "Show more" chip under a paged list. */
export function ShowMore(props: { shown: number; total: number; onMore: () => void }) {
  if (props.shown >= props.total) return null;
  return (
    <div style={{ padding: '12px 0' }}>
      <button type="button" class="chip" data-testid="show-more" onClick={props.onMore}>
        {t('list_showMore', { shown: num(props.shown), total: num(props.total) })}
      </button>
    </div>
  );
}

/** This month / last month / all time / a specific month. Shared across the entry lists. */
export function MonthPicker(props: { months: ReadonlyArray<string> }) {
  const p = listPeriod.value;
  const set = (next: ListPeriod) => (listPeriod.value = next);
  const chip = (kind: 'this' | 'last' | 'all', label: string) => (
    <button
      type="button"
      class="chip"
      aria-pressed={p.kind === kind}
      onClick={() => set({ kind })}
      data-testid={'period-' + kind}
    >
      {label}
    </button>
  );
  return (
    <div class="chips" role="group" data-testid="month-picker">
      {chip('this', t('period_thisMonth'))}
      {chip('last', t('period_lastMonth'))}
      {chip('all', t('period_allTime'))}
      <select
        class="chip"
        value={p.kind === 'month' ? p.month : ''}
        onChange={(e) => {
          const v = (e.target as HTMLSelectElement).value;
          if (v) set({ kind: 'month', month: v });
        }}
        aria-pressed={p.kind === 'month'}
        data-testid="period-month"
      >
        <option value="">{t('period_pick')}</option>
        {props.months.map((m) => (
          <option key={m} value={m}>
            {mLong(m)}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Label for the current list period, e.g. "September 2026" or "all time". */
export function periodLabel(): string {
  const p = listPeriod.value;
  if (p.kind === 'all') return t('period_allTime');
  if (p.kind === 'month') return mLong(p.month);
  return t(p.kind === 'this' ? 'period_thisMonth' : 'period_lastMonth');
}

export function SearchBox(props: {
  value: string;
  onInput: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div class="search">
      <TextInput
        value={props.value}
        onInput={props.onInput}
        placeholder={props.placeholder}
        type="search"
        data-testid="list-search"
      />
    </div>
  );
}
